import type { ActivityType, DateOnly, DocumentCategory, FundingStatus } from '../api/types'

export const RUPEES_PER_CRORE = 10_000_000

/** 4125000000 → "₹412.5 Cr" (Indian digit grouping, up to 2 decimals). */
export function formatCrore(rupees: number): string {
  const crore = rupees / RUPEES_PER_CRORE
  return `₹${crore.toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`
}

/**
 * "2026-10-05" → a Date at local midnight.
 * (new Date("2026-10-05") would mean midnight UTC, which can show as the wrong day.)
 */
export function parseDateOnly(value: DateOnly): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** Date → "2026-10-05", the format the API and <input type="date"> use. */
export function toDateOnly(date: Date): DateOnly {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const dateFormat = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })

/** "2026-10-05" → "5 Oct 2026"; null → "—". */
export function formatDate(value: DateOnly | null): string {
  return value ? dateFormat.format(parseDateOnly(value)) : '—'
}

const timeFormat = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })

/** A timestamp from the API → "21 Aug 2026" in the viewer's time zone. */
export function formatTimestampDate(iso: string): string {
  return dateFormat.format(new Date(iso))
}

/** A timestamp from the API → "20 Sept 2026, 3:30 pm" in the viewer's time zone. */
export function formatDateTime(iso: string): string {
  const date = new Date(iso)
  return `${dateFormat.format(date)}, ${timeFormat.format(date)}`
}

/** 1 → "1 day", 3 → "3 days". */
export function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

export const FUNDING_LABELS: Record<FundingStatus, string> = {
  Approved: 'Approved',
  NeedToFind: 'Need to Find',
  NotSure: 'Not Sure',
  NotApplicable: 'Not Applicable',
}

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  TenderDocument: 'Tender document',
  Proposal: 'Proposal',
  Correspondence: 'Correspondence',
  Contract: 'Contract',
  MeetingNotes: 'Meeting notes',
  Other: 'Other',
}

/** 2048 → "2 KB", 3500000 → "3.3 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const ACTIVITY_LABELS: Record<ActivityType, string> = {
  Call: 'Call',
  Meeting: 'Meeting',
  Email: 'Email',
  SiteVisit: 'Site visit',
  Comment: 'Comment',
  Update: 'Update',
  Note: 'Note',
  Other: 'Other',
}
